import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Cookie, Settings } from 'lucide-react';

export function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="cookies-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#00D95F] rounded-full flex items-center justify-center mx-auto mb-6">
            <Cookie className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-xl text-gray-600">Last updated: January 27, 2026</p>
        </div>
      </div>

      <div className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              This Cookie Policy explains how GiriChat ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website and use our services.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              By using our website, you consent to the use of cookies in accordance with this Cookie Policy.
            </p>
          </div>

          {/* Quick Overview */}
          <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What You Should Know</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-[#00D95F] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">We Use Cookies</div>
                  <div className="text-sm text-gray-700">To improve your experience on our website</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-[#00D95F] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">You Control Cookies</div>
                  <div className="text-sm text-gray-700">Manage preferences in your browser settings</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are Cookies?</h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to the owners of the site.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cookies allow us to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our website</li>
                <li>Improve your experience on our platform</li>
                <li>Provide personalized content and features</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Deliver relevant advertisements</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Cookie className="w-8 h-8 text-[#00D95F]" />
                Types of Cookies We Use
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Essential Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility.
                  </p>
                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-700"><strong>Examples:</strong></p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 mt-2 space-y-1">
                      <li>Authentication cookies (keep you logged in)</li>
                      <li>Security cookies (prevent fraud)</li>
                      <li>Session cookies (remember your actions)</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Can you opt-out?</strong> No - These are required for the website to work.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Performance Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies collect information about how you use our website, such as which pages you visit most often and if you receive error messages. This helps us improve the performance of our website.
                  </p>
                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-700"><strong>Examples:</strong></p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 mt-2 space-y-1">
                      <li>Google Analytics cookies</li>
                      <li>Page load time tracking</li>
                      <li>Error tracking cookies</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Can you opt-out?</strong> Yes - These cookies are optional.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Functional Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies allow our website to remember choices you make (such as your language preference) and provide enhanced, personalized features.
                  </p>
                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-700"><strong>Examples:</strong></p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 mt-2 space-y-1">
                      <li>Language preference cookies</li>
                      <li>Region selection cookies</li>
                      <li>Chat widget cookies</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Can you opt-out?</strong> Yes - But some features may not work properly.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Advertising Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies are used to deliver advertisements that are relevant to you and your interests. They also limit the number of times you see an advertisement and help measure the effectiveness of advertising campaigns.
                  </p>
                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-700"><strong>Examples:</strong></p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 mt-2 space-y-1">
                      <li>Google Ads cookies</li>
                      <li>Facebook Pixel</li>
                      <li>Retargeting cookies</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Can you opt-out?</strong> Yes - These cookies are optional.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Third-Party Cookies</h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                In addition to our own cookies, we use various third-party cookies to report usage statistics and deliver advertisements. These third parties may use cookies to collect information about your online activities across different websites.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Third-Party Services We Use:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                    <div>
                      <strong className="text-gray-900">Google Analytics</strong>
                      <p className="text-sm text-gray-600">Helps us understand how visitors use our website</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                    <div>
                      <strong className="text-gray-900">Facebook Pixel</strong>
                      <p className="text-sm text-gray-600">Enables us to measure advertising effectiveness</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                    <div>
                      <strong className="text-gray-900">Intercom</strong>
                      <p className="text-sm text-gray-600">Provides customer support chat functionality</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                    <div>
                      <strong className="text-gray-900">Stripe</strong>
                      <p className="text-sm text-gray-600">Processes payments securely</p>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Settings className="w-8 h-8 text-[#00D95F]" />
                How to Control Cookies
              </h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                You have the right to decide whether to accept or reject cookies. You can control cookies through your browser settings and other tools.
              </p>

              <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Browser Settings</h3>
                <p className="text-gray-700 mb-4">
                  Most web browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                  <li>• <strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                  <li>• <strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                  <li>• <strong>Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Opt-Out Links</h3>
                <p className="text-gray-700 mb-3">
                  You can also opt-out of specific cookie tracking:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• <a href="https://tools.google.com/dlpage/gaoptout" className="text-[#00D95F] hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
                  <li>• <a href="https://www.facebook.com/ads/preferences" className="text-[#00D95F] hover:underline" target="_blank" rel="noopener noreferrer">Facebook Ad Preferences</a></li>
                  <li>• <a href="http://optout.networkadvertising.org" className="text-[#00D95F] hover:underline" target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a></li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <p className="text-sm text-gray-700">
                  <strong>⚠️ Note:</strong> Blocking or deleting cookies may prevent you from using certain features of our website and may affect your user experience.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] border border-[#00D95F]/20 rounded-lg p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Email</h4>
                    <p className="text-gray-700">support@girichat.com</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Phone</h4>
                    <p className="text-gray-700">+1 (555) 123-4567</p>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="font-semibold text-gray-900 mb-3">Mailing Address</h4>
                    <p className="text-gray-700">
                      GiriChat Privacy Team<br />
                      A32 Juhu Sun N Sea bldg<br />
                      Juhu Tara Rd, Santacruz West<br />
                      Mumbai 400049, India
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
